import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ReactElement, ReactNode } from 'react';
import { Color } from 'shared/utils/colors';
import type { ChartId } from '../catalog/chartCatalog';
import { CHART_ICONS } from '../catalog/chartIcons';
import { CHART_FONT_FAMILY } from '../theme/muiChartSlotProps';

const titleSx = {
  fontFamily: CHART_FONT_FAMILY,
  fontSize: '1rem',
  fontWeight: 600,
  lineHeight: '21px',
  m: 0,
  color: 'text.primary',
} satisfies SxProps<Theme>;

const subtitleSx = {
  fontFamily: CHART_FONT_FAMILY,
  fontSize: '0.8125rem',
  fontWeight: 400,
  lineHeight: '18px',
  mt: 0.5,
  mb: 0,
  color: 'text.secondary',
} satisfies SxProps<Theme>;

const titleWithIconSx = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1.5,
} satisfies SxProps<Theme>;

/** The chart's icon in a pale brand circle, as on the dashboard design. */
const chartIconBadgeSx = {
  width: 36,
  height: 36,
  borderRadius: '50%',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: Color.primaryOrangePale,
  color: Color.black,
  '& svg': {
    fontSize: '1.25rem',
  },
} satisfies SxProps<Theme>;

const cardHeaderRowSx = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 2,
  flexWrap: 'wrap',
} satisfies SxProps<Theme>;

const metaRowSx = {
  display: 'flex',
  flexFlow: 'row wrap',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 1.5,
  flexShrink: 0,
} satisfies SxProps<Theme>;

const lastUpdateSx = {
  fontFamily: CHART_FONT_FAMILY,
  fontSize: '0.8125rem',
  fontWeight: 400,
  lineHeight: '18px',
  color: 'text.secondary',
  m: 0,
} satisfies SxProps<Theme>;

type ChartCardHeadingProps = Readonly<{
  title: string;
  subtitle?: string;
  titleComponent?: 'h1' | 'h2';
  lastUpdate?: string;
  actions?: ReactNode;
  /** Shows the chart's icon before the title. */
  chartId?: ChartId;
}>;

function ChartIconBadge({ chartId }: Readonly<{ chartId: ChartId }>): ReactElement {
  const Icon = CHART_ICONS[chartId];
  return (
    <Box sx={chartIconBadgeSx} aria-hidden>
      <Icon />
    </Box>
  );
}

export function ChartCardHeading({
  title,
  subtitle,
  titleComponent = 'h2',
  lastUpdate,
  actions,
  chartId,
}: ChartCardHeadingProps): ReactElement {
  const texts = (
    <>
      <Typography component={titleComponent} sx={titleSx}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography component='p' sx={subtitleSx}>
          {subtitle}
        </Typography>
      ) : null}
    </>
  );
  const titleBlock = chartId ? (
    <Box sx={titleWithIconSx}>
      <ChartIconBadge chartId={chartId} />
      <Box sx={{ minWidth: 0 }}>{texts}</Box>
    </Box>
  ) : (
    texts
  );

  if (!lastUpdate && !actions) {
    return <Box sx={{ flexShrink: 0 }}>{titleBlock}</Box>;
  }

  return (
    <Box sx={cardHeaderRowSx}>
      <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>{titleBlock}</Box>
      <Box sx={metaRowSx}>
        {lastUpdate ? (
          <Typography component='p' sx={lastUpdateSx}>
            {lastUpdate}
          </Typography>
        ) : null}
        {actions}
      </Box>
    </Box>
  );
}
